// ─── Public Status Page — incident filters ───────────────────────────
//
// Seven multi-select chips (OR'd together — an incident matches when ANY of its
// labels is selected) plus a free-text search. App.tsx owns the state and does
// the matching; this component owns the controls and the URL.
//
// WHY THE URL: the reason anyone filters this list is to send it to somebody
// else — "look, every outage on the payment component". So the selection is
// mirrored into ?f=outage,monitoring with history.replaceState (replace, not
// push: ticking a chip is not a navigation step and must not fill the Back
// button with filter permutations), and a URL that already carries ?f= hydrates
// the selection on first mount. The value is written in canonical
// STATUS_FILTER_LABELS order so the same selection always produces the same URL.
//
// The chips are <button aria-pressed> inside a role="group", not checkboxes:
// they are toggles that act immediately, and there is no form to submit.

import { useEffect, useRef } from 'react';
import { STATUS_FILTER_LABELS, type StatusFilterLabel } from '@pcp/shared';
import { da } from '../i18n/da';
import { fmtNumber } from '../lib/format';
import type { IncidentFiltersProps } from '../types';
import { SIcon } from '../ui/SIcon';

/** Query parameter carrying the chip selection. */
const FILTER_PARAM = 'f';

function isFilterLabel(value: string): value is StatusFilterLabel {
  return (STATUS_FILTER_LABELS as readonly string[]).includes(value);
}

/** Read ?f=… into a canonically-ordered, de-duplicated, validated list. */
function readFilterParam(): StatusFilterLabel[] {
  try {
    const raw = new URLSearchParams(window.location.search).get(FILTER_PARAM);
    if (!raw) return [];
    const wanted = new Set(raw.split(',').map((part) => part.trim().toLowerCase()).filter(isFilterLabel));
    return STATUS_FILTER_LABELS.filter((label) => wanted.has(label));
  } catch {
    return [];
  }
}

function writeFilterParam(selected: readonly StatusFilterLabel[]): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const ordered = STATUS_FILTER_LABELS.filter((label) => selected.includes(label));
    if (ordered.length > 0) params.set(FILTER_PARAM, ordered.join(','));
    else params.delete(FILTER_PARAM);

    const search = params.toString();
    const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) window.history.replaceState(null, '', next);
  } catch {
    /* A history API that refuses the write must never break the filters. */
  }
}

export function IncidentFilters({
  options,
  selected,
  onToggle,
  onClear,
  query,
  onQueryChange,
}: IncidentFiltersProps) {
  // Both refs survive StrictMode's double mount, so hydration runs exactly once
  // and can never toggle a label back off on the second pass.
  const hydratedRef = useRef(false);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const current = selectedRef.current;
    for (const label of readFilterParam()) {
      if (!current.includes(label)) onToggle(label);
    }
  }, [onToggle]);

  // Skips the first pass: on mount the URL is the source of truth, not the
  // (still empty) selection, and writing here would erase what we just read.
  const wroteOnceRef = useRef(false);
  useEffect(() => {
    if (!wroteOnceRef.current) {
      wroteOnceRef.current = true;
      return;
    }
    writeFilterParam(selected);
  }, [selected]);

  const hasActiveFilter = selected.length > 0 || query.trim().length > 0;

  return (
    <div className="sp-filters">
      <div className="sp-filter-chips" role="group" aria-label={da.filterHeading}>
        {options.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              className={active ? 'sp-filter-chip is-active' : 'sp-filter-chip'}
              aria-pressed={active}
              onClick={() => onToggle(option.value)}
            >
              {option.label}
              <span className="sp-filter-count">{fmtNumber(option.count)}</span>
            </button>
          );
        })}

        {hasActiveFilter && (
          <button type="button" className="sp-btn sp-btn--ghost sp-btn--sm sp-filter-clear" onClick={onClear}>
            <SIcon name="x" size={14} />
            {da.filterClear}
          </button>
        )}
      </div>

      <div className="sp-search">
        <SIcon name="search" size={16} className="sp-search-icon" />
        <input
          type="search"
          className="sp-search-input"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder={da.searchPlaceholder}
          aria-label={da.searchLabel}
          autoComplete="off"
        />
      </div>
    </div>
  );
}
